import { ApplicationRef, ErrorHandler, Inject, NgModule } from '@angular/core'
import { CDK_DRAG_CONFIG } from '@angular/cdk/drag-drop'
import { BlockScrollStrategy, Overlay } from '@angular/cdk/overlay'
import { APP_BASE_HREF } from '@angular/common'
import { HttpClientJsonpModule, HttpClientModule } from '@angular/common/http'
import { FormsModule } from '@angular/forms'
import { MAT_MENU_SCROLL_STRATEGY } from '@angular/material/menu'
import { BrowserModule } from '@angular/platform-browser'
import { BrowserAnimationsModule } from '@angular/platform-browser/animations'
import { NavigationError, Router, RouteReuseStrategy, RouterModule } from '@angular/router'
import { NgbModal, NgbPopoverModule, NgbTooltipConfig, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap'
import { TranslateService } from '@ngx-translate/core'
import { WebStorageModule } from 'ngx-store'
import { ToastrModule } from 'ngx-toastr'

import { BootstrapData } from 'elements-sdk'
import { BOOTSTRAP_DATA } from 'api/providers'
import { NOW, RXJS_SCHEDULER } from 'common/helpers'
import { LocaleModule } from 'common/locale'
import { DatabaseProfilerService, NotifyService, RouteLoggerService, SocketService, UpgradeDetectorService, UtilCommonModule } from 'common/util'
import { MetricsService } from 'common/util/services/metrics.service'
import { UtilElementsClientModule } from 'common/util.elementsClient'
import { UtilFileConflictModule } from 'common/util.fileConflict'
import { UtilPickersModule } from 'common/util.pickers'
import { UtilReleaseNotesModule } from 'common/util.releaseNotes'
import { MainModule } from 'main'
import { ParametersService } from 'main/services/parameters.service'
import { ClickModule } from 'click'
import { HelpModule } from 'help'
import { MediaCustomFieldsModule } from 'media.customFields'
import { MediaPermissionsModule } from 'media.permissions'
import { MediaSharingModule } from 'media.sharing'
import { UploaderModule } from 'uploader'
import { PartnersService } from 'licensing/components/partners.service'
import { partners } from 'main.about/partners.json'

import { FourOhFourView } from './components/404View.component'
import { AppComponent } from './components/app.component'
import { AppRootComponent } from './components/appRoot.component'
import { AppRootOutletComponent } from './components/appRootOutlet.component'
import { IndexViewComponent } from './components/indexView.component'
import { NavigationErrorViewComponent } from './components/navigationErrorView.component'
import { CustomReuseStrategy, observeNavigationStates } from './routeReuseStrategy'
import { AngularErrorHandler } from './services/errorHandler.service'

export function scrollFactory (overlay: Overlay): () => BlockScrollStrategy {
  return () => overlay.scrollStrategies.block()
}

@NgModule({
  imports: [
    RouterModule.forRoot([
      {
        path: 'auth',
        loadChildren: () => import(/* webpackChunkName: "app.main" */ 'auth'),
      },
      {
        path: 'auth/logged-out',
        loadComponent: () => import('root/components/loggedOutView.component'),
      },
      {
        path: 'update-in-progress',
        loadComponent: () => import('updateInProgress').then(m => m.UpdateInProgressViewComponent),
      },
      {
        path: 'licensing',
        loadChildren: () => import(/* webpackChunkName: "app.licensing" */ 'licensing').then(m => m.ROUTES),
      },
      {
        path: 'download-redirect',
        loadComponent: () => import('root/components/downloadRedirectView.component'),
      },
      {
        path: 'self-close',
        loadComponent: () => import('root/components/selfCloseView.component'),
      },
      {
        ...AppRootOutletComponent.ROUTE,
        children: [
          {
            path: '',
            component: IndexViewComponent,
            pathMatch: 'full',
          },
          {
            path: 'status',
            loadChildren: () => import(/* webpackChunkName: "app.status" */ 'status').then(m => m.ROUTES),
          },
          {
            path: 'production',
            loadChildren: () => import(/* webpackChunkName: "app.storage.production" */ 'storage.production'),
          },
          {
            path: 'workspace',
            loadChildren: () => import(/* webpackChunkName: "app.storage.workspace" */ 'storage.workspace'),
          },
          {
            path: 'share',
            loadChildren: () => import(/* webpackChunkName: "app.storage.share" */ 'storage.share'),
          },
          {
            path: 'file-manager',
            loadChildren: () => import(/* webpackChunkName: "app.storage.filemanager" */ 'storage.filemanager'),
          },
          {
            path: 'workstation',
            loadChildren: () => import(/* webpackChunkName: "app.main.workstation" */ 'main.workstation'),
          },
          {
            path: 'download',
            loadChildren: () => import(/* webpackChunkName: "app.download" */ 'download').then(m => m.ROUTES),
          },
          {
            path: 'download-archive',
            loadChildren: () => import(/* webpackChunkName: "app.downloadArchive" */ 'downloadArchive').then(m => m.ROUTES),
          },
          {
            path: 'system/volumes',
            loadChildren: () => import(/* webpackChunkName: "app.system.volumes" */ 'system.volumes'),
          },
          {
            path: 'system/nodes',
            loadChildren: () => import(/* webpackChunkName: "app.system.nodes" */ 'system.nodes').then(m => m.ROUTES),
          },
          {
            path: 'system',
            loadChildren: () => import(/* webpackChunkName: "app.system" */ 'system'),
          },
          {
            path: 'tasks',
            loadChildren: () => import(/* webpackChunkName: "app.tasks" */ 'tasks'),
          },
          {
            path: 'media/browse',
            loadChildren: () => import(/* webpackChunkName: "media.browse" */ 'media.browse'),
          },
          {
            path: 'media/root',
            loadChildren: () => import(/* webpackChunkName: "media.root" */ 'media.root').then(m => m.ROUTES),
          },
          {
            path: 'media/upload',
            loadChildren: () => import(/* webpackChunkName: "media.upload" */ 'media.upload').then(m => m.ROUTES),
          },
          {
            path: 'media/compare',
            loadChildren: () => import(/* webpackChunkName: "media.compare" */ 'media.compare').then(m => m.ROUTES),
          },
          {
            path: 'media/view',
            loadChildren: () => import(/* webpackChunkName: "media.view" */ 'media.view').then(m => m.ROUTES),
          },
          {
            path: 'media/bundle',
            loadChildren: () => import(/* webpackChunkName: "media.view" */ 'media.view').then(m => m.BUNDLE_ROUTES),
          },
          {
            path: 'media/playlist',
            loadChildren: () => import(/* webpackChunkName: "media.playlist" */ 'media.view').then(m => m.PLAYLIST_ROUTES),
          },
          {
            path: 'media/search',
            loadChildren: () => import(/* webpackChunkName: "media.search" */ 'media.search').then(m => m.ROUTES),
          },
          {
            path: 'media/shared-items',
            loadChildren: () => import(/* webpackChunkName: "media.sharedItems" */ 'media.sharedItems').then(m => m.ROUTES),
          },
          {
            path: 'media/editor',
            loadChildren: () => import(/* webpackChunkName: "media.editor" */ 'media.editor').then(m => m.ROUTES),
          },
          {
            path: 'media/subtitle',
            loadChildren: () => import(/* webpackChunkName: "media.subtitles" */ 'media.subtitles').then(m => m.ROUTES),
          },
          {
            path: 'media',
            loadChildren: () => import(/* webpackChunkName: "media.home" */ 'media.home').then(m => m.ROUTES),
          },
          {
            path: 'about',
            loadChildren: () => import(/* webpackChunkName: "main.about" */ 'main.about').then(m => m.ROUTES),
          },
          {
            path: 'user',
            loadChildren: () => import(/* webpackChunkName: "main.users" */ 'main.users').then(m => m.ROUTES),
          },
          {
            path: 'profile',
            loadChildren: () => import(/* webpackChunkName: "main.profile" */ 'main.profile'),
          },
          {
            path: 'archive/library',
            loadChildren: () => import(/* webpackChunkName: "tape.library" */ 'tape.library'),
          },
          {
            path: 'archive/search',
            loadChildren: () => import(/* webpackChunkName: "tape.search" */ 'tape.search'),
          },
          {
            path: 'archive/jobs',
            loadChildren: () => import(/* webpackChunkName: "tape.jobs" */ 'tape.jobs'),
          },
          {
            path: 'archive/start',
            loadChildren: () => import(/* webpackChunkName: "tape.archive" */ 'tape.archive'),
          },
          {
            path: 'archive/restore',
            loadChildren: () => import(/* webpackChunkName: "tape.restore" */ 'tape.restore'),
          },
          {
            path: 'archive/tapes',
            loadChildren: () => import(/* webpackChunkName: "tape.tapes" */ 'tape.tapes'),
          },
          {
            path: 'ui-test',
            loadChildren: () => import(/* webpackChunkName: "ui-test" */ 'ui-test').then(m => m.ROUTES),
          },
        ],
      },
      {
        path: 'not-found',
        component: FourOhFourView,
      },
      {
        path: 'navigation-error',
        component: NavigationErrorViewComponent,
      },
      {
        path: '**',
        pathMatch: 'full',
        component: FourOhFourView,
      },
    ], {
      onSameUrlNavigation: 'reload',
    }),
    BrowserModule,
    BrowserAnimationsModule,
    UtilCommonModule,
    UtilPickersModule,
    UtilReleaseNotesModule,
    UtilFileConflictModule,
    UtilElementsClientModule,
    MediaPermissionsModule,
    MediaCustomFieldsModule,
    MediaSharingModule,
    MainModule,
    ClickModule,
    HelpModule,
    UploaderModule,
    NgbPopoverModule,
    NgbTooltipModule,
    FormsModule,
    HttpClientModule,
    HttpClientJsonpModule,
    LocaleModule.forRoot(),
    WebStorageModule,
    ToastrModule.forRoot({
      positionClass: 'toast-bottom-right',
    }),
    AppRootComponent,
    FourOhFourView,
  ],
  providers: [
    { provide: APP_BASE_HREF, useValue: '/' },
    { provide: RouteReuseStrategy, useExisting: CustomReuseStrategy },
    { provide: ErrorHandler, useClass: AngularErrorHandler },
    { provide: MAT_MENU_SCROLL_STRATEGY, useFactory: scrollFactory, deps: [Overlay] },
    {
      provide: CDK_DRAG_CONFIG,
      useValue: {
        dragStartDelay: {
          touch: 1000,
          mouse: 0,
        },
      },
    },
    { provide: RXJS_SCHEDULER, useValue: undefined },
    { provide: NOW, useValue: () => Date.now() },
    { provide: PartnersService, useValue: new PartnersService(partners) },
  ],
})
export class AppModule {
  constructor (
    private appRef: ApplicationRef,
    private ngbModal: NgbModal,
    private router: Router,
    private routeLogger: RouteLoggerService,
    private ngbTooltipConfig: NgbTooltipConfig,
    private metrics: MetricsService,
    private parameters: ParametersService,
    _databaseProfiler: DatabaseProfilerService,
    _upgradeDetector: UpgradeDetectorService,
    @Inject(BOOTSTRAP_DATA) private bootstrapData: BootstrapData,
    socket: SocketService,
    notify: NotifyService,
    translate: TranslateService,
  ) {
    observeNavigationStates()

    ngbTooltipConfig.openDelay = 500
    ngbTooltipConfig.placement = 'top top-start top-end auto'

    socket.subject('auth:saml-test-success').subscribe(data => {
      notify.success(
        translate.instant('Success'),
        translate.instant('Logged in as { user }', { user: data.user.display_name }),
      )
    })

    socket.subject('auth:saml-test-fail').subscribe(data => {
      notify.error(translate.instant('Error'), data.error)
    })
  }

  async ngDoBootstrap () {
    // Restore window.requestAnimationFrame temporarily so that angular-animate uses the original function
    (window as any)['requestAnimationFrame'] = window[window['Zone'].__symbol__('requestAnimationFrame')]

    this.appRef.bootstrap(AppComponent)

    this.router.events.subscribe(event => {
      if (event instanceof NavigationError) {
        this.routeLogger.handleNavigationError(event.error, event.url)
      }
    })

    if (this.bootstrapData.parameterValues?.analytics && this.bootstrapData.sentryConfig.dsn) {
      this.metrics.enableSentry(
        this.bootstrapData.sentryConfig,
        this.bootstrapData.identityValue
          ? {
            id: this.bootstrapData.identityValue.id.toString(),
            email: this.bootstrapData.identityValue.email!,
            username: this.bootstrapData.identityValue.username,
          }
          : null,
      )
    }
  }
}
